<div class="w-full flex flex-col h-full min-h-[500px]">
  <!-- Controls section -->
  <div *ngIf="showControls" class="w-full bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md mb-4">
    <form [formGroup]="dateRangeForm" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Date range preset selection -->
      <div class="col-span-1 lg:col-span-4">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
        <div class="flex flex-wrap gap-2">
          <button 
            *ngFor="let preset of dateRangePresets"
            type="button"
            (click)="dateRangeForm.get('preset')?.setValue(preset.value)"
            [class]="'px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ' + 
              (dateRangeForm.get('preset')?.value === preset.value ? 
              'bg-indigo-600 text-white hover:bg-indigo-700' : 
              'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50')"
          >
            {{preset.label}}
          </button>
        </div>
        <!-- Show selected date range info -->
        <div *ngIf="dateRangeForm.get('preset')?.value !== 'custom'" class="mt-2 text-sm text-gray-500">
          {{dateRangeForm.get('startDate')?.value | date}} - {{dateRangeForm.get('endDate')?.value | date}}
        </div>
      </div>

      <!-- Custom date range selection (shown only when Custom is selected) -->
      <ng-container *ngIf="dateRangeForm.get('preset')?.value === 'custom'">
        <div class="col-span-1 lg:col-span-4 mb-2">
          <p class="text-sm text-amber-600 dark:text-amber-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 inline-block mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
            Note: Data is only available from January 1, 2024 onwards.
          </p>
        </div>
        <div class="col-span-1">
          <label for="startDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
          <input 
            type="date" 
            id="startDate" 
            formControlName="startDate"
            min="2024-01-01"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
        </div>
        
        <div class="col-span-1">
          <label for="startTime" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
          <input 
            type="time" 
            id="startTime" 
            formControlName="startTime"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
        </div>
        
        <div class="col-span-1">
          <label for="endDate" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
          <input 
            type="date" 
            id="endDate" 
            formControlName="endDate"
            min="2024-01-01"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
        </div>
        
        <div class="col-span-1">
          <label for="endTime" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
          <input 
            type="time" 
            id="endTime" 
            formControlName="endTime"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
        </div>
      </ng-container>
      
      <!-- Aggregation controls -->
      <div class="col-span-1 md:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-3 mt-2">
        <div>
          <label for="aggregation" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time Grouping</label>
          <select 
            id="aggregation" 
            formControlName="aggregation"
            class="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option *ngFor="let option of aggregationOptions" [value]="option.value">{{option.label}}</option>
          </select>
        </div>
        
        <!-- <div class="flex items-center ml-4">
          <input 
            type="checkbox" 
            id="realtime" 
            formControlName="realtime"
            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          >
          <label for="realtime" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">Real-time Updates</label>
        </div> -->
        
        <div class="flex-grow"></div>
        
        <div class="flex gap-2">
          <button 
            type="button" 
            (click)="refreshData()" 
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
            </svg>
            Refresh
          </button>
        </div>
      </div>
    </form>
  </div>
  
  <!-- Toggle controls button -->
  <div class="flex justify-end mb-2">
    <button 
      type="button" 
      (click)="toggleControls()" 
      class="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      <span *ngIf="showControls">Hide Controls</span>
      <span *ngIf="!showControls">Show Controls</span>
    </button>
  </div>

  <!-- Statistics Section -->
  <div *ngIf="!loading && !error && chartData.series.length > 0" class="w-full bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md mb-4">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Power Statistics</h3>
    <div class="grid grid-cols-2 gap-4">
      <div class="p-4 bg-green-50 dark:bg-green-900 rounded-lg">
        <div class="text-green-800 dark:text-green-200 text-sm font-medium">Total ON Time</div>
        <div class="text-xl font-bold text-green-900 dark:text-green-100 whitespace-pre-line">{{totalOnHours}}</div>
      </div>
      <div class="p-4 bg-red-50 dark:bg-red-900 rounded-lg">
        <div class="text-red-800 dark:text-red-200 text-sm font-medium">Total OFF Time</div>
        <div class="text-xl font-bold text-red-900 dark:text-red-100 whitespace-pre-line">{{totalOffHours}}</div>
      </div>
    </div>
  </div>
  
  <!-- Loading indicator -->
  <div *ngIf="loading" class="w-full flex justify-center items-center py-8">
    <div class="flex flex-col items-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      <p class="mt-4 text-gray-700 dark:text-gray-300">Loading data...</p>
    </div>
  </div>
  
  <!-- Error message -->
  <div *ngIf="error && !loading" class="w-full bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-4">
    <div class="flex">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
      </svg>
      <span>{{ error }}</span>
    </div>
  </div>
  
  <!-- Chart container -->
  <div class="flex-grow relative">
    <div 
      #chartContainer 
      echarts 
      [options]="chartOption" 
      (chartInit)="onChartInit($event)" 
      class="w-full h-full"
    ></div>
    <!-- Loading overlay -->
    <div *ngIf="loading" class="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  </div>
  
  <!-- Zoom controls -->
  <div *ngIf="!loading && !error" class="mt-4 flex justify-center gap-2">
    <button 
      type="button" 
      (click)="setZoomLevel('minute')" 
      [class.bg-indigo-600]="dateRangeForm.value.aggregation === 'minute'"
      [class.text-white]="dateRangeForm.value.aggregation === 'minute'"
      [class.bg-white]="dateRangeForm.value.aggregation !== 'minute'"
      class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      Minute
    </button>
    <button 
      type="button" 
      (click)="setZoomLevel('hour')" 
      [class.bg-indigo-600]="dateRangeForm.value.aggregation === 'hour'"
      [class.text-white]="dateRangeForm.value.aggregation === 'hour'"
      [class.bg-white]="dateRangeForm.value.aggregation !== 'hour'"
      class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      Hour
    </button>
    <button 
      type="button" 
      (click)="setZoomLevel('day')" 
      [class.bg-indigo-600]="dateRangeForm.value.aggregation === 'day'"
      [class.text-white]="dateRangeForm.value.aggregation === 'day'"
      [class.bg-white]="dateRangeForm.value.aggregation !== 'day'"
      class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      Day
    </button>
  </div>
  
  <!-- Notice for empty data -->
  <div *ngIf="!loading && !error && chartData.xAxis.length === 0" class="w-full flex justify-center items-center py-8">
    <p class="text-gray-500 dark:text-gray-400">No data available for the selected time range. Try adjusting your filters.</p>
  </div>
</div>
